import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modeSelect", "newSection", "existingSection", "existingSelect", "requiredField", "requiredSkillsContainer"]
  static values = {
    initialMode: { type: String, default: "existing" }
  }

  connect() {
    this.updateVisibility()
    this.validateSelection()
  }

  toggle() {
    this.updateVisibility()
    this.validateSelection()
  }

  validateSelection() {
    const mode = this.modeSelectTarget.value

    if (mode === "existing") {
      // Validate existing job description selection
      const hasSelection = this.hasExistingSelectTarget && this.existingSelectTarget.value !== ""
      this.updateSubmitButton(!hasSelection)
    } else if (mode === "new") {
      // Validate new job description required fields
      const isValid = this.validateNewJobDescriptionFields()
      this.updateSubmitButton(!isValid)
    }
  }

  validateNewJobDescriptionFields() {
    // Check all required fields have values
    const requiredFieldsValid = this.requiredFieldTargets.every(field => {
      return field.value.trim() !== ""
    })

    // Check at least one required skill has a value
    const hasRequiredSkill = this.hasAtLeastOneRequiredSkill()

    return requiredFieldsValid && hasRequiredSkill
  }

  hasAtLeastOneRequiredSkill() {
    if (!this.hasRequiredSkillsContainerTarget) return true

    const skillInputs = this.requiredSkillsContainerTarget.querySelectorAll('input[name="new_job_description[required_skills][]"]')
    return Array.from(skillInputs).some(input => input.value.trim() !== "")
  }

  get submitButton() {
    // Find the submit button by traversing up to the form
    const form = this.element.closest("form")
    return form ? form.querySelector('input[type="submit"], button[type="submit"]') : null
  }

  updateSubmitButton(disabled) {
    const button = this.submitButton
    if (button) {
      button.disabled = disabled
      if (disabled) {
        button.classList.add("opacity-50", "cursor-not-allowed")
        button.classList.remove("hover:bg-indigo-700")
      } else {
        button.classList.remove("opacity-50", "cursor-not-allowed")
        button.classList.add("hover:bg-indigo-700")
      }
    }
  }

  updateVisibility() {
    const mode = this.modeSelectTarget.value

    if (mode === "new") {
      this.showNewSection()
      this.hideExistingSection()
      this.clearExistingSelection()
    } else if (mode === "existing") {
      this.hideNewSection()
      this.showExistingSection()
      this.clearNewFields()
    }
  }

  showNewSection() {
    if (this.hasNewSectionTarget) {
      this.newSectionTarget.classList.remove("hidden")
      this.enableInputs(this.newSectionTarget)
    }
  }

  hideNewSection() {
    if (this.hasNewSectionTarget) {
      this.newSectionTarget.classList.add("hidden")
      this.disableInputs(this.newSectionTarget)
    }
  }

  showExistingSection() {
    if (this.hasExistingSectionTarget) {
      this.existingSectionTarget.classList.remove("hidden")
      this.enableInputs(this.existingSectionTarget)
    }
  }

  hideExistingSection() {
    if (this.hasExistingSectionTarget) {
      this.existingSectionTarget.classList.add("hidden")
      this.disableInputs(this.existingSectionTarget)
    }
  }

  clearExistingSelection() {
    if (this.hasExistingSelectTarget) {
      this.existingSelectTarget.value = ""
    }
  }

  clearNewFields() {
    if (this.hasNewSectionTarget) {
      const inputs = this.newSectionTarget.querySelectorAll("input, textarea, select")
      inputs.forEach(input => {
        if (input.type === "checkbox" || input.type === "radio") {
          input.checked = false
        } else {
          input.value = ""
        }
      })
    }
  }

  enableInputs(container) {
    const inputs = container.querySelectorAll("input, textarea, select")
    inputs.forEach(input => {
      input.disabled = false
    })
  }

  disableInputs(container) {
    const inputs = container.querySelectorAll("input, textarea, select")
    inputs.forEach(input => {
      input.disabled = true
    })
  }
}
